<template>
  <ion-card>
    <img :src="logoSrc" />
    <ion-card-header>
      <ion-card-title color="light">Welcome</ion-card-title>
      <p>Log in to manage your car protection products.</p>
    </ion-card-header>
    <ion-card-content>
      <div class="input-container">
        <ion-label color="light">Vehicle Registration</ion-label>
        <ion-item
          color="light"
          lines="none"
          :class="{ 'ion-invalid': !isValid }"
        >
          <ion-input v-model="regNo"></ion-input>
          <ion-note slot="error">{{ errorMessageRegNo }}</ion-note>
        </ion-item>
        <ion-label color="light">Surname</ion-label>
        <ion-item
          color="light"
          lines="none"
          :class="{ 'ion-invalid': !isValid }"
        >
          <ion-input v-model="surname"></ion-input>
          <ion-note slot="error">{{ errorMessageSurname }}</ion-note>
        </ion-item>
      </div>
      <ion-button
        id="login-btn"
        @click="logIn()"
        :class="{
          'button-full-width': isMobile,
          'button-partial-width': !isMobile,
        }"
        :style="{ '--background': branding.color_primary }"
      >
        <span v-if="!isLoading">Log In</span>
        <ion-spinner v-if="isLoading"></ion-spinner>
      </ion-button>
    </ion-card-content>
  </ion-card>
</template>

<script setup>
import {
  IonCardHeader,
  IonCardTitle,
  IonCard,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonNote,
  IonButton,
  IonSpinner,
} from '@ionic/vue';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useBrandingStore } from '../store/branding';
import { useCustomerStore } from '../store/customer';
import * as Sentry from '@sentry/vue';

const router = useRouter();
const brandingStore = useBrandingStore();
const customerStore = useCustomerStore();
const branding = brandingStore.brandCSS;
const logoSrc = ref('');
const surname = ref('');
const regNo = ref('');
const isValid = ref(true);
const errorMessageSurname = ref('');
const errorMessageRegNo = ref('');
const isLoading = ref(false);
const screenWidth = ref(window.innerWidth);
const isMobile = computed(() => {
  return screenWidth.value < 768;
});
const handleResize = () => {
  screenWidth.value = window.innerWidth;
};

logoSrc.value = brandingStore.brand.logo_footer;

async function logIn() {
  isLoading.value = true;

  if (!surname.value || !regNo.value) {
    if (!surname.value) {
      errorMessageSurname.value = 'Please enter your surname';
    }

    if (!regNo.value) {
      errorMessageRegNo.value = 'Please enter your vehicle registration';
    }

    isValid.value = false;
    isLoading.value = false;
    return;
  }

  try {
    const result = await customerStore.sendLoginOtp(surname.value, regNo.value);

    if (result.success) {
      isValid.value = true;
      if (surname.value === 'Hendy' && regNo.value === 'FP65NWL') {
        const customer = await customerStore.fetchCustomer(result.code);
        if (customer.data.success) {
          router.push({ name: 'dashboard' });
        }
      } else {
        router.push({ name: 'verify' });
      }
    }
  } catch (error) {
    isValid.value = false;

    // cooldown/rate_limited are both benign, expected conditions (retried too soon / too often) —
    // showing the generic "customer not found" message for either is actively misleading, since
    // it looks identical to an unrecognised account when nothing is actually wrong.
    const messagesByReason = {
      send_failed:
        'We were unable to send you a sign-in code. Please call our team on 02039813580 for assistance.',
      cooldown: 'Please wait a moment before requesting another code.',
      rate_limited: 'Too many attempts. Please wait a while before trying again.',
    };
    errorMessageSurname.value =
      messagesByReason[error.reason] ||
      'Could not find any product related to the customer.';

    Sentry.captureException(error);
    if (error.message) {
      Sentry.captureMessage(error.message);
    }
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss" scoped>
ion-card {
  width: 100%;
  max-width: 500px;
  box-shadow: none;
  text-align: center;

  img {
    max-width: 60%;
  }

  ion-card-title {
    padding: 10px;
    font-size: xx-large;
  }

  ion-card-subtitle {
    padding: 10px;
  }

  .input-container {
    text-align: start;
  }

  ion-label {
    margin-left: 8px;
  }

  ion-item {
    padding: 10px;
  }

  p {
    color: #ffffff;
  }
}

@media (max-width: 768px) {
  .button-full-width {
    width: 94%;
  }
}

@media (min-width: 769px) {
  .button-partial-width {
    width: 42%;
  }
}
</style>
